import React, { useEffect, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { supabase } from '../../../shared/lib/supabase';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { useMatches } from '../../../shared/hooks/useMatches';
import { MatchWithTeams } from '../../../shared/types/database';
import { matchVideosService } from '../../../shared/services/matchVideosService';

interface UploadVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  userId: string;
  currentFolderPath: string;
  onUploadComplete?: () => void;
}

/** UUID v4 pattern – matchId must be matches.id from DB, never display name */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const UploadVideoModal: React.FC<UploadVideoModalProps> = ({
  isOpen,
  onClose,
  orgId,
  userId,
  onUploadComplete,
}) => {
  const [uploadMethod, setUploadMethod] = useState<'local' | 'link'>('local');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [quality, setQuality] = useState<'fullhd' | 'hd'>('fullhd');
  const [sourceUrl, setSourceUrl] = useState('');
  const [matchId, setMatchId] = useState<string>('');
  const [errors, setErrors] = useState<{ displayName?: string; sourceUrl?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparedVideo, setPreparedVideo] = useState<{
    videoId: string;
    storagePath: string;
    uploadInstructions: string;
    isLinkUpload?: boolean;
  } | null>(null);

  const [matchIdsWithLinkedVideo, setMatchIdsWithLinkedVideo] = useState<Set<string>>(new Set());

  // Fetch matches for match selector
  const { matches, loading: matchesLoading } = useMatches();

  // Fetch match IDs that already have a video linked (for filtering dropdown)
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      const result = await matchVideosService.getMatchIdsWithLinkedVideo();
      if (result.success) setMatchIdsWithLinkedVideo(new Set(result.data));
      else setMatchIdsWithLinkedVideo(new Set());
    };
    load();
  }, [isOpen]);

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      // Reject YouTube URLs
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        return false;
      }
      
      // Reject Vimeo URLs
      if (hostname.includes('vimeo.com')) {
        return false;
      }
      
      // Must be HTTP or HTTPS
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { displayName?: string; sourceUrl?: string } = {};
    
    if (!displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    
    if (uploadMethod === 'link') {
      if (!sourceUrl.trim()) {
        newErrors.sourceUrl = 'Video URL is required';
      } else if (!validateUrl(sourceUrl.trim())) {
        newErrors.sourceUrl = 'Invalid URL. YouTube and Vimeo URLs are not supported. Please provide a direct download link.';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePrepareUpload = async () => {
    if (!validateForm()) {
      return;
    }

    setIsPreparing(true);
    setErrors({});
    setApiError(null);

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      if (uploadMethod === 'link') {
        // Only send matchId if it's a valid UUID and exists in the loaded matches list (same as dropdown)
        const validMatchId =
          matchId &&
          UUID_REGEX.test(matchId) &&
          matches.some((m) => m.id === matchId)
            ? matchId
            : undefined;

        const baseUrl = (process.env.REACT_APP_SUPABASE_URL || 'https://api.kridah.com').replace(/\/$/, '');
        const response = await fetch(`${baseUrl}/functions/v1/prepare-video-upload-from-link`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orgId,
            fileName: displayName.trim(),
            description: description.trim() || undefined,
            quality,
            sourceUrl: sourceUrl.trim(),
            matchId: validMatchId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || `Failed to prepare upload from link: HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!data || !data.videoId || !data.jobId) {
          throw new Error('Invalid response from server');
        }

        setPreparedVideo({
          videoId: data.videoId,
          storagePath: data.storagePath || '',
          uploadInstructions: `Video upload job has been queued. The worker will download the video from the provided URL and upload it to R2. You can track the progress in the video details.`,
          isLinkUpload: true,
        });
      } else {
        // Call prepare-video-upload Edge Function (local upload)
        const { data, error } = await supabase.functions.invoke('prepare-video-upload', {
          body: {
            orgId,
            fileName: displayName.trim(),
            description: description.trim() || undefined,
            quality,
            matchId: matchId || undefined,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (error) {
          throw new Error(error.message || 'Failed to prepare upload');
        }

        if (!data || !data.videoId || !data.storagePath) {
          throw new Error('Invalid response from server');
        }

        setPreparedVideo({
          videoId: data.videoId,
          storagePath: data.storagePath,
          uploadInstructions: data.uploadInstructions || `Upload your file to R2 at: ${data.storagePath}`,
          isLinkUpload: false,
        });
      }

      // Notify parent that upload was prepared
      if (onUploadComplete) {
        setTimeout(() => {
          onUploadComplete();
        }, 500);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to prepare upload';
      setApiError(errorMessage);
      console.error('Prepare upload error:', error);
    } finally {
      setIsPreparing(false);
    }
  };

  const handleClose = () => {
    setUploadMethod('local');
    setDisplayName('');
    setDescription('');
    setQuality('fullhd');
    setSourceUrl('');
    setMatchId('');
    setErrors({});
    setApiError(null);
    setPreparedVideo(null);
    onClose();
  };

  // Format match name for display: include match name when set, then teams and date
  const formatMatchName = (match: MatchWithTeams): string => {
    const homeTeam = match.home_team?.name || 'Home Team';
    const awayTeam = match.away_team?.name || 'Away Team';
    const matchDate = match.start_date ? new Date(match.start_date).toLocaleDateString() : '';
    const teamsAndDate = `${homeTeam} vs ${awayTeam}${matchDate ? ` - ${matchDate}` : ''}`;
    const matchName = match.name?.trim();
    return matchName ? `${matchName} — ${teamsAndDate}` : teamsAndDate;
  };

  // Prepare match options for dropdown: exclude matches that already have a video linked
  const matchOptions = matches
    .filter((match) => !matchIdsWithLinkedVideo.has(match.id))
    .map((match) => ({
      id: match.id,
      name: formatMatchName(match),
    }));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Prepare Video Upload" className="max-w-2xl">
      <div className="p-6 space-y-4 pb-24">
            {!preparedVideo ? (
              <>
                {/* Backend/API error – dedicated area, not inline */}
                {apiError && (
                  <div
                    className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800"
                    role="alert"
                  >
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Upload failed</p>
                      <p className="text-sm mt-0.5">{apiError}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setApiError(null)}
                      className="flex-shrink-0 p-1 rounded hover:bg-red-100 text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label="Dismiss error"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Upload Method Selector */}
                <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Upload Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setUploadMethod('local');
                    setSourceUrl('');
                    setErrors((prev) => ({ ...prev, sourceUrl: undefined }));
                  }}
                  className={`px-4 py-3 border-2 rounded-lg text-left transition-colors cursor-pointer ${
                    uploadMethod === 'local'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Upload Local File</p>
                      <p className="text-xs text-gray-500 mt-0.5">Upload from your computer</p>
                    </div>
                    {uploadMethod === 'local' && (
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUploadMethod('link');
                    setErrors((prev) => ({ ...prev, sourceUrl: undefined }));
                  }}
                  className={`px-4 py-3 border-2 rounded-lg text-left transition-colors cursor-pointer ${
                    uploadMethod === 'link'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Upload from Link</p>
                      <p className="text-xs text-gray-500 mt-0.5">Google Drive, Dropbox, etc.</p>
                    </div>
                    {uploadMethod === 'link' && (
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Metadata Form */}
            <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Display Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (errors.displayName) setErrors((prev) => ({ ...prev, displayName: undefined }));
                if (apiError) setApiError(null);
              }}
              placeholder="Enter a name for this video"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.displayName ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.displayName && (
              <p className="mt-1 text-xs text-red-600">{errors.displayName}</p>
            )}
          </div>

          {uploadMethod === 'link' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Video URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => {
                  setSourceUrl(e.target.value);
                  if (errors.sourceUrl) setErrors((prev) => ({ ...prev, sourceUrl: undefined }));
                  if (apiError) setApiError(null);
                }}
                placeholder="https://drive.google.com/... or direct video URL"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.sourceUrl ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.sourceUrl && (
                <p className="mt-1 text-xs text-red-600">{errors.sourceUrl}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Provide a direct download link. YouTube and Vimeo URLs are not supported.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Video Quality
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setQuality('fullhd')}
                    className={`px-4 py-3 border-2 rounded-lg text-left transition-colors cursor-pointer ${
                  quality === 'fullhd'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                    }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Full HD</p>
                  </div>
                  {quality === 'fullhd' && (
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setQuality('hd')}
                    className={`px-4 py-3 border-2 rounded-lg text-left transition-colors cursor-pointer ${
                  quality === 'hd'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                    }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">HD</p>
                  </div>
                  {quality === 'hd' && (
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              HD quality reduces file size while maintaining good visual quality
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Link to Match <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <SearchableDropdown
              value={matchId}
              onChange={(id) => {
                setMatchId(id);
                if (apiError) setApiError(null);
              }}
              options={matchOptions}
              placeholder="Select a match (optional)"
              loading={matchesLoading}
              showClearIcon={true}
              onClear={() => {
                setMatchId('');
                if (apiError) setApiError(null);
              }}
            />
            <p className="mt-1 text-xs text-gray-500">
              Link this video to a match for event tagging and highlights. The video will be linked after transcoding completes.
            </p>
          </div>
        </div>
              </>
            ) : (
              <>
                {preparedVideo.isLinkUpload ? (
                  /* Link Upload Success */
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                      <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-green-900 mb-2">
                          Upload Started Successfully
                        </h3>
                        <p className="text-sm text-green-800 mb-3">
                          Your video upload job has been queued. The worker will download the video from the provided URL and upload it to R2 automatically. You can track the progress in the video details.
                        </p>
                        {matchId && (
                          <p className="text-sm text-green-700 mt-2">
                            ✓ Video will be linked to match after transcoding completes
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Local Upload - Direct Link */
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">
                      Ready to Upload
                    </h3>
                    <p className="text-sm text-blue-800 mb-4">
                      Click the button below to open the upload page. You can upload files of any size (including 10GB+).
                    </p>
                    <button
                      onClick={() => {
                        const uploadUrl = `/o/${orgId}/capture/upload/${preparedVideo.videoId}`;
                        window.open(uploadUrl, '_blank');
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Open Upload Page</span>
                    </button>
                    <p className="text-xs text-blue-700 mt-3">
                      You can access this upload page anytime from the video details page.
                    </p>
                  </div>
                )}
              </>
            )}
      </div>

      {/* Sticky Footer with Buttons - Always visible at bottom */}
      <div className="sticky bottom-0 border-t border-gray-200 bg-white p-6 -mx-6 -mb-6 mt-auto">
          {!preparedVideo ? (
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePrepareUpload}
                disabled={isPreparing || !displayName.trim() || (uploadMethod === 'link' && !sourceUrl.trim())}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPreparing ? 'Preparing...' : uploadMethod === 'link' ? 'Start Upload' : 'Prepare Upload'}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
    </Modal>
  );
};

export default UploadVideoModal;
